import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { STATUTS, BESOINS } from "@/data/staticContent";
import { useT } from "@/context/ContentContext";
import { Phone, EnvelopeSimple, MapPin, PaperPlaneRight } from "@phosphor-icons/react";

const initialState = {
  nom: "", prenom: "", telephone: "", email: "",
  statut: "particulier", besoin: "assurance_pro",
  message: "", rgpd: false,
};

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const successText = useT("contact.form.success");
  const submitLabel = useT("contact.form.submit");
  const rgpdText = useT("contact.form.rgpd");
  const messagePh = useT("contact.form.placeholder");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.rgpd) { toast.error("Merci d'accepter le traitement de vos données."); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      toast.success(successText);
      setForm(initialState);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-ducs-navy border border-ducs-gold/25 px-4 py-3 text-ducs-cream placeholder-ducs-mist/60 focus:border-ducs-gold transition-colors rounded-sm";
  const labelCls = "label-caps block mb-2";

  return (
    <div data-testid="page-contact">
      <section className="relative py-24 md:py-32 border-b border-ducs-gold/10">
        <div className="absolute inset-0 dijon-pattern opacity-30" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <div className="label-caps mb-5">{useT("contact.hero.eyebrow")}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ducs-cream leading-tight tracking-tight max-w-4xl">
            {useT("contact.hero.title.line1")}<br />
            <span className="text-ducs-gold italic">{useT("contact.hero.title.accent")}</span>
          </h1>
          <div className="gold-divider mt-6" />
          <p className="mt-6 text-ducs-mist text-base md:text-lg leading-relaxed max-w-2xl whitespace-pre-line">
            {useT("contact.hero.intro")}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4 space-y-8">
            <div>
              <div className="label-caps mb-3">{useT("contact.aside.eyebrow")}</div>
              <div className="font-serif text-2xl text-ducs-cream">{useT("contact.aside.name")}</div>
              <div className="text-ducs-mist text-sm mt-1">{useT("contact.aside.role")}</div>
            </div>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <MapPin size={20} className="text-ducs-gold mt-0.5" />
                <span className="text-ducs-cream/90">{useT("contact.aside.location")}</span>
              </div>
              <div className="flex items-start gap-3">
                <EnvelopeSimple size={20} className="text-ducs-gold mt-0.5" />
                <a className="text-ducs-cream/90 hover:text-ducs-gold" href={`mailto:${useT("contact.aside.email")}`}>
                  {useT("contact.aside.email")}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Phone size={20} className="text-ducs-gold mt-0.5" />
                <span className="text-ducs-cream/90">{useT("contact.aside.phone")}</span>
              </div>
            </div>
            <div className="border-l-2 border-ducs-gold/50 pl-5">
              <div className="label-caps mb-2">{useT("contact.aside.note.label")}</div>
              <p className="text-ducs-mist text-sm leading-relaxed whitespace-pre-line">{useT("contact.aside.note.text")}</p>
            </div>
          </aside>

          <form onSubmit={onSubmit} data-testid="contact-form" className="lg:col-span-8 card-ducs p-6 md:p-10">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className={labelCls} htmlFor="prenom">Prénom</label>
                <input id="prenom" required data-testid="input-prenom" value={form.prenom} onChange={(e) => update("prenom", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="nom">Nom</label>
                <input id="nom" required data-testid="input-nom" value={form.nom} onChange={(e) => update("nom", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="telephone">Téléphone</label>
                <input id="telephone" required data-testid="input-telephone" type="tel" value={form.telephone} onChange={(e) => update("telephone", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="email">Email</label>
                <input id="email" required data-testid="input-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="statut">Vous êtes</label>
                <select id="statut" data-testid="select-statut" value={form.statut} onChange={(e) => update("statut", e.target.value)} className={inputCls}>
                  {STATUTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select></div>
              <div><label className={labelCls} htmlFor="besoin">Votre besoin principal</label>
                <select id="besoin" data-testid="select-besoin" value={form.besoin} onChange={(e) => update("besoin", e.target.value)} className={inputCls}>
                  {BESOINS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select></div>
            </div>
            <div className="mt-5">
              <label className={labelCls} htmlFor="message">Message</label>
              <textarea id="message" rows={5} data-testid="input-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder={messagePh} className={inputCls} />
            </div>

            <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="rgpd-label">
              <input type="checkbox" required data-testid="input-rgpd" checked={form.rgpd} onChange={(e) => update("rgpd", e.target.checked)} className="mt-1 h-4 w-4 accent-[#D4A744]" />
              <span className="text-sm text-ducs-mist leading-relaxed">{rgpdText}</span>
            </label>

            <button type="submit" disabled={submitting} data-testid="contact-submit-button" className="btn-primary mt-8 disabled:opacity-60">
              <PaperPlaneRight size={16} weight="bold" />
              {submitting ? "Envoi en cours…" : submitLabel}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
